import type {
  DocElement,
  ExportDocument,
  ExportSection,
  InlineContent,
  SectionPageHint,
} from './document-model';

export class DocumentBuilder {
  private readonly documentTitle: string;
  private readonly sections: ExportSection[] = [];
  private currentSection: ExportSection;

  constructor(title: string) {
    this.documentTitle = title;
    this.currentSection = { pageHint: 'main', elements: [] };
    this.sections.push(this.currentSection);
  }

  startSection(pageHint: SectionPageHint): this {
    if (this.currentSection.elements.length === 0 && this.currentSection.pageHint === pageHint) {
      return this;
    }

    this.currentSection = { pageHint, elements: [] };
    this.sections.push(this.currentSection);
    return this;
  }

  add(element: DocElement): this {
    this.currentSection.elements.push(element);
    return this;
  }

  heading(level: 1 | 2 | 3, content: InlineContent): this {
    return this.add({ type: 'heading', level, content });
  }

  paragraph(content: InlineContent): this {
    return this.add({ type: 'paragraph', content });
  }

  listItem(content: InlineContent, indent = 0): this {
    return this.add({ type: 'list-item', indent, content });
  }

  blockquote(content: InlineContent): this {
    return this.add({ type: 'blockquote', content });
  }

  image(src: string, alt: string): this {
    return this.add({ type: 'image', src, alt });
  }

  hr(): this {
    return this.add({ type: 'hr' });
  }

  build(): ExportDocument {
    const nonEmpty = this.sections.filter((s) => s.elements.length > 0);

    return {
      title: this.documentTitle,
      sections: nonEmpty.length > 0 ? nonEmpty : [{ pageHint: 'main', elements: [] }],
    };
  }
}

export const text = (value: string): InlineContent => [{ type: 'text', value }];

export const raw = (value: string): InlineContent => [{ type: 'raw', value }];

export const bold = (value: string): InlineContent => [{ type: 'bold', value }];

export const italic = (value: string): InlineContent => [{ type: 'italic', value }];

export const code = (value: string): InlineContent => [{ type: 'code', value }];

export const link = (label: string, href: string): InlineContent => [
  { type: 'link', label, href },
];

export const spans = (...parts: InlineContent[]): InlineContent => parts.flat();
